import { Platform } from "react-native";
import { type IThemePalette } from "../types";
import { MaterialShapes } from "../base";

/**
 * Canonical button padding tokens across standard and action button densities.
 */
export const ButtonPaddings = {
  standard: {
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  action: {
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  compact: {
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  large: {
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
} as const;

/**
 * Visual tokens for a button variant.
 */
export interface IButtonVariantTokens {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  hoverBorderColor: string;
  progressFillColor: string;
}

/**
 * Geometric and typography tokens for a button size density.
 */
export interface IButtonSizeTokens {
  paddingVertical: number;
  paddingHorizontal: number;
  fontSize: number;
  iconSize: number;
  gap: number;
  minHeight: number;
}

/**
 * Complete component theme definition for ThemedButton.
 */
export interface IButtonTheme {
  variants: {
    primary: IButtonVariantTokens;
    secondary: IButtonVariantTokens;
    danger: IButtonVariantTokens;
    minimal: IButtonVariantTokens;
  };
  sizes: {
    sm: IButtonSizeTokens;
    compact: IButtonSizeTokens;
    md: IButtonSizeTokens;
    lg: IButtonSizeTokens;
  };
  /** Default horizontal padding for standard buttons (10px) */
  paddingHorizontal: number;
  /** Default vertical padding for standard buttons (5px) */
  paddingVertical: number;
  /** Action / compact button horizontal padding for cards and item rows (6px) */
  actionPaddingHorizontal: number;
  /** Action / compact button vertical padding for cards and item rows (3px) */
  actionPaddingVertical: number;
  borderRadius: number;
  borderWidth: number;
  disabledOpacity: number;
  progressBarHeight: number;
}

/**
 * Pure factory generating the button component theme based on active palette and mode.
 * Strictly adheres to the Purple Ban and 2px static border invariant.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved IButtonTheme.
 */
export function getButtonTheme(palette: IThemePalette, isDark: boolean): IButtonTheme {
  return {
    variants: {
      primary: {
        backgroundColor: palette.button,
        borderColor: "transparent",
        textColor: palette.buttonText,
        hoverBorderColor: palette.buttonHoverBorder,
        progressFillColor: isDark ? palette.buttonText : "#FFFFFF",
      },
      secondary: {
        backgroundColor: palette.buttonSecondary,
        borderColor: palette.buttonSecondaryBorder,
        textColor: palette.buttonSecondaryText,
        hoverBorderColor: palette.buttonSecondaryHoverBorder,
        progressFillColor: palette.tint,
      },
      danger: {
        backgroundColor: palette.danger,
        borderColor: "transparent",
        textColor: palette.buttonDangerText,
        hoverBorderColor: palette.buttonDangerHoverBorder,
        progressFillColor: isDark ? palette.buttonDangerText : "#FFFFFF",
      },
      minimal: {
        backgroundColor: "transparent",
        borderColor: palette.border,
        textColor: palette.text,
        hoverBorderColor: palette.text,
        progressFillColor: palette.tint,
      },
    },
    sizes: {
      sm: {
        paddingVertical: ButtonPaddings.action.paddingVertical,
        paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
        fontSize: 11,
        iconSize: 12,
        gap: 4,
        minHeight: 24,
      },
      compact: {
        paddingVertical: ButtonPaddings.compact.paddingVertical,
        paddingHorizontal: ButtonPaddings.compact.paddingHorizontal,
        fontSize: 12,
        iconSize: 13,
        gap: 4,
        minHeight: 26,
      },
      md: {
        paddingVertical: ButtonPaddings.standard.paddingVertical,
        paddingHorizontal: ButtonPaddings.standard.paddingHorizontal,
        fontSize: 13,
        iconSize: 15,
        gap: 6,
        minHeight: 32,
      },
      lg: {
        paddingVertical: ButtonPaddings.large.paddingVertical,
        paddingHorizontal: ButtonPaddings.large.paddingHorizontal,
        fontSize: 14,
        iconSize: 16,
        gap: 8,
        minHeight: 40,
      },
    },
    paddingHorizontal: ButtonPaddings.standard.paddingHorizontal,
    paddingVertical: ButtonPaddings.standard.paddingVertical,
    actionPaddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    actionPaddingVertical: ButtonPaddings.action.paddingVertical,
    borderRadius: Platform.select({ android: MaterialShapes.small, default: 4 }),
    borderWidth: 2,
    disabledOpacity: 0.5,
    progressBarHeight: 3,
  };
}
